import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Search, SlidersHorizontal, X, RefreshCw } from 'lucide-react';

export const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'a_vencer', label: 'A vencer' },
  { value: 'vencido', label: 'Vencidos' },
  { value: 'pago', label: 'Pagos' },
  { value: 'parcial', label: 'Parciais' },
  { value: 'cancelado', label: 'Cancelados' },
  { value: 'renegociado', label: 'Renegociados' },
];

export const PERIOD_OPTIONS = [
  { value: 'all', label: 'Qualquer período' },
  { value: 'hoje', label: 'Hoje' },
  { value: 'mes', label: 'Este mês' },
  { value: 'mes_anterior', label: 'Mês anterior' },
  { value: 'prox30', label: 'Próximos 30 dias' },
  { value: 'ano', label: 'Este ano' },
  { value: 'custom', label: 'Personalizado' },
];

export const CATEGORY_OPTIONS = [
  { value: 'all', label: 'Todas as categorias' },
  { value: 'mensalidade', label: 'Mensalidade Escolar' },
  { value: 'material', label: 'Material Didático' },
  { value: 'bilingue', label: 'Programa Bilíngue' },
  { value: 'integral', label: 'Período Integral' },
  { value: 'matricula', label: 'Matrícula/Rematrícula' },
  { value: 'outros', label: 'Outros' },
];

export const SOURCE_OPTIONS = [
  { value: 'all', label: 'Todas as origens' },
  { value: 'safra', label: 'Safra' },
  { value: 'import', label: 'Importação de Títulos' },
  { value: 'rematricula', label: 'Matrícula/Rematrícula' },
  { value: 'plan', label: 'Plano Financeiro' },
  { value: 'manual', label: 'Manual' },
  { value: 'cnab', label: 'CNAB' },
];

export const QUICK_CHIPS = [
  { value: '', label: 'Todos', facet: 'all' },
  { value: 'hoje', label: 'Hoje', facet: 'hoje' },
  { value: '7d', label: 'Vence em 7 dias', facet: '7d' },
  { value: '30d', label: 'Vence em 30 dias', facet: '30d' },
  { value: 'vencidos', label: 'Vencidos', facet: 'vencidos' },
  { value: 'pagos', label: 'Pagos', facet: 'pagos' },
  { value: 'rematricula', label: 'Matrícula/Rematrícula', facet: 'rematricula' },
  { value: 'sem_boleto', label: 'Sem boleto', facet: 'sem_boleto' },
  { value: 'safra', label: 'Safra', facet: 'safra' },
  { value: 'cnab', label: 'CNAB', facet: 'cnab' },
  { value: 'sem_plano', label: 'Sem plano', facet: 'sem_plano' },
];

const money = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const FieldsGrid = ({ filters, set, classGroups }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
    <div className="xl:col-span-2">
      <Label className="text-[10px] uppercase text-slate-500">Aluno, responsável, matrícula, CPF, nosso número</Label>
      <div className="relative mt-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
        <Input className="pl-9" placeholder="Busca inteligente..." value={filters.q}
          onChange={(e) => set({ q: e.target.value })} data-testid="titulos-filter-search" />
      </div>
    </div>
    <div>
      <Label className="text-[10px] uppercase text-slate-500">Status</Label>
      <Select value={filters.status} onValueChange={(v) => set({ status: v })}>
        <SelectTrigger className="mt-1" data-testid="titulos-filter-status"><SelectValue /></SelectTrigger>
        <SelectContent>{STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
    <div>
      <Label className="text-[10px] uppercase text-slate-500">Período</Label>
      <Select value={filters.period} onValueChange={(v) => set({ period: v })}>
        <SelectTrigger className="mt-1" data-testid="titulos-filter-period"><SelectValue /></SelectTrigger>
        <SelectContent>{PERIOD_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
    <div>
      <Label className="text-[10px] uppercase text-slate-500">Categoria</Label>
      <Select value={filters.category} onValueChange={(v) => set({ category: v })}>
        <SelectTrigger className="mt-1" data-testid="titulos-filter-category"><SelectValue /></SelectTrigger>
        <SelectContent>{CATEGORY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
    <div>
      <Label className="text-[10px] uppercase text-slate-500">Origem</Label>
      <Select value={filters.source} onValueChange={(v) => set({ source: v })}>
        <SelectTrigger className="mt-1" data-testid="titulos-filter-source"><SelectValue /></SelectTrigger>
        <SelectContent>{SOURCE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
    <div>
      <Label className="text-[10px] uppercase text-slate-500">Turma</Label>
      <Select value={filters.class_group_id} onValueChange={(v) => set({ class_group_id: v })}>
        <SelectTrigger className="mt-1" data-testid="titulos-filter-class"><SelectValue placeholder="Todas as turmas" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todas as turmas</SelectItem>
          {classGroups.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    {filters.period === 'custom' && (
      <>
        <div>
          <Label className="text-[10px] uppercase text-slate-500">Vencimento de</Label>
          <Input type="date" className="mt-1" value={filters.date_from}
            onChange={(e) => set({ date_from: e.target.value })} data-testid="titulos-filter-from" />
        </div>
        <div>
          <Label className="text-[10px] uppercase text-slate-500">Vencimento até</Label>
          <Input type="date" className="mt-1" value={filters.date_to}
            onChange={(e) => set({ date_to: e.target.value })} data-testid="titulos-filter-to" />
        </div>
      </>
    )}
  </div>
);

export const TitulosFilters = ({ filters, set, reset, facets, summary, classGroups, dirty, onRefresh }) => (
  <div className="space-y-3">
    {/* Desktop */}
    <div className="hidden md:block rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <FieldsGrid filters={filters} set={set} classGroups={classGroups} />
    </div>

    {/* Mobile — filtros em drawer */}
    <div className="md:hidden flex gap-2">
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="outline" className="flex-1" data-testid="titulos-filters-mobile-btn">
            <SlidersHorizontal className="w-4 h-4 mr-2" /> Filtros
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
          <SheetHeader><SheetTitle>Filtros</SheetTitle></SheetHeader>
          <div className="mt-4"><FieldsGrid filters={filters} set={set} classGroups={classGroups} /></div>
        </SheetContent>
      </Sheet>
      <Button variant="outline" onClick={onRefresh} data-testid="titulos-refresh-mobile">
        <RefreshCw className="w-4 h-4" />
      </Button>
    </div>

    {/* Chips rápidos */}
    <div className="flex gap-2 overflow-x-auto pb-1" data-testid="titulos-quick-chips">
      {QUICK_CHIPS.map(chip => {
        const count = facets?.[chip.facet];
        const on = (filters.quick || '') === chip.value;
        return (
          <button key={chip.label} type="button" onClick={() => set({ quick: chip.value })}
            data-testid={`chip-${chip.facet}`}
            className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition-all duration-200
              ${on ? 'border-violet-600 bg-violet-600 text-white shadow' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300 hover:text-violet-700'}`}>
            {chip.label}{count !== undefined && <span className={`ml-1.5 ${on ? 'text-white/80' : 'text-slate-400'}`}>{count}</span>}
          </button>
        );
      })}
    </div>

    {/* Barra de resumo dos filtros */}
    {dirty && (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-violet-200 bg-violet-50 px-4 py-2.5"
           data-testid="titulos-filter-summary">
        <p className="text-sm text-violet-900">
          Exibindo <b>{summary?.count ?? 0}</b> títulos · <b>{money(summary?.open)}</b> em aberto · <b>{money(summary?.received)}</b> recebido
        </p>
        <Button size="sm" variant="ghost" onClick={reset} className="text-violet-700 hover:bg-violet-100"
                data-testid="titulos-clear-filters">
          <X className="w-3.5 h-3.5 mr-1" /> Limpar filtros
        </Button>
      </div>
    )}
  </div>
);

export default TitulosFilters;
